import bikeConditionQueries from "$/lib/queries/bike-condition-queries.ts"
import jobQueries from "$/lib/queries/job-queries.ts"
import receiptQueries from "$/lib/queries/receipt-queries.ts"
import workOrderQueries, {
  allTasksDoneQueryOptions,
} from "$/lib/queries/work-order-queries.ts"
import {
  AddonOrderDraftResponse,
  AddonOrderHistory,
  AddonOrderProposal,
  AddonOrderProposalWithReceipt,
  AddonOrderSubmissionResult,
  DataRequiredForAddonOrder,
  Route,
  SaveAddonOrderDraft,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

async function fetchRequiredData(jobId: string) {
  return request<DataRequiredForAddonOrder>({
    url: populateRoute(Route.ADDON_ORDER__GET_REQUIRED_DATA, jobId),
    method: "GET",
  })
}

async function fetchOrderHistory(jobId: string) {
  return request<AddonOrderHistory>({
    url: populateRoute(Route.ADDON_ORDER__GET_HISTORY, jobId),
    method: "GET",
  })
}

async function fetchProposalWithReceipt(jobId: string) {
  return request<AddonOrderProposalWithReceipt>({
    url: populateRoute(Route.ADDON_ORDER__GET_PROPOSAL, jobId),
    method: "GET",
  })
}

async function sendAddonProposal(
  jobId: string,
  proposal: AddonOrderProposal,
): Promise<AddonOrderSubmissionResult> {
  return request<AddonOrderSubmissionResult>({
    url: populateRoute(Route.ADDON_ORDER__SEND_PROPOSAL, jobId),
    method: "POST",
    data: proposal,
  })
}

async function fetchDraft(jobId: string) {
  return request<AddonOrderDraftResponse>({
    url: populateRoute(Route.ADDON_ORDER__GET_DRAFT, jobId),
    method: "GET",
  })
}

async function saveDraft(jobId: string, content: SaveAddonOrderDraft) {
  return request<AddonOrderDraftResponse>({
    url: populateRoute(Route.ADDON_ORDER__SAVE_DRAFT, jobId),
    method: "PUT",
    data: content,
  })
}

async function deleteDraft(jobId: string) {
  return request<void>({
    url: populateRoute(Route.ADDON_ORDER__DELETE_DRAFT, jobId),
    method: "DELETE",
  })
}

async function revokeAddonOrder(jobId: string) {
  return request<{ success: boolean }>({
    url: populateRoute(Route.ADDON_ORDER__REVOKE, jobId),
    method: "POST",
  })
}

export function useAddonOrderMutations(jobId: string) {
  const queryClient = useQueryClient()

  const onSettled = async () => {
    await Promise.all([
      queryClient.invalidateQueries(workOrderQueries.detail(jobId)),
      queryClient.invalidateQueries(allTasksDoneQueryOptions(jobId)),
      queryClient.invalidateQueries(receiptQueries.detail(jobId)),
      queryClient.invalidateQueries(addonOrderQueries.detail(jobId)),
      queryClient.invalidateQueries(addonOrderQueries.history(jobId)),
      queryClient.invalidateQueries(addonOrderQueries.proposal(jobId)),
      queryClient.invalidateQueries(addonOrderQueries.draft(jobId)),
      queryClient.invalidateQueries(bikeConditionQueries.detail(jobId)),
      queryClient.invalidateQueries(jobQueries.detail(jobId)),
    ])
  }

  const sendAddonProposalMutation = useMutation({
    mutationFn: (proposal: AddonOrderProposal) =>
      sendAddonProposal(jobId, proposal),
    onSettled,
  })

  // The form keeps its own state after a save, so the saved draft is written
  // straight into the cache instead of refetched.
  const saveDraftMutation = useMutation({
    mutationFn: (content: SaveAddonOrderDraft) => saveDraft(jobId, content),
    onSuccess: (response) => {
      queryClient.setQueryData(
        addonOrderQueries.draft(jobId).queryKey,
        response,
      )
    },
  })

  const deleteDraftMutation = useMutation({
    mutationFn: () => deleteDraft(jobId),
    onSuccess: () => {
      queryClient.setQueryData(addonOrderQueries.draft(jobId).queryKey, {
        draft: null,
      })
    },
  })

  // Withdrawing removes the pending items and restores the sent order as the
  // draft, so everything the order touches is refetched.
  const revokeMutation = useMutation({
    mutationFn: () => revokeAddonOrder(jobId),
    onSuccess: async () => {
      await Promise.all([
        onSettled(),
        queryClient.invalidateQueries({ queryKey: ["jobs"] }),
      ])
    },
  })

  return {
    sendAddonProposalMutation,
    saveDraftMutation,
    deleteDraftMutation,
    revokeMutation,
  }
}

const addonOrderQueries = {
  all: () => ["addon-order"],
  details: () => [...addonOrderQueries.all(), "detail"],
  detail: (jobId: string) =>
    queryOptions({
      queryKey: [...addonOrderQueries.details(), jobId],
      queryFn: () => fetchRequiredData(jobId),
    }),
  histories: () => [...addonOrderQueries.all(), "history"],
  history: (jobId: string) =>
    queryOptions({
      queryKey: [...addonOrderQueries.histories(), jobId],
      queryFn: () => fetchOrderHistory(jobId),
    }),
  proposals: () => [...addonOrderQueries.all(), "proposal"],
  proposal: (jobId: string) =>
    queryOptions({
      queryKey: [...addonOrderQueries.proposals(), jobId],
      queryFn: () => fetchProposalWithReceipt(jobId),
    }),
  drafts: () => [...addonOrderQueries.all(), "draft"],
  draft: (jobId: string) =>
    queryOptions({
      queryKey: [...addonOrderQueries.drafts(), jobId],
      queryFn: () => fetchDraft(jobId),
    }),
}

export default addonOrderQueries
